/**
 * DailyQuiz.tsx
 * Drop-in component for GrindLog's Daily Challenge tab.
 * Matches the existing dark-theme CSS pattern of the app.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createClient } from '@supabase/supabase-js'
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL!,
  process.env.REACT_APP_SUPABASE_ANON_KEY!
)
// ─── Types ─────────────────────────────────────────────────────────────────────

interface ActionStep {
  street: 'preflop' | 'flop' | 'turn' | 'river'
  board: string | null
  action: string
}

interface QuizExercise {
  id: string
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  spot_type: 'SRP' | '3BP' | 'multiway'
  question_type: string
  position_hero: string
  position_villain: string
  stack_depth: number
  show_hole_cards: boolean
  hole_cards: string | null
  scenario: string
  action_sequence: ActionStep[]
  question: string
  options: string[] | null
  correct_answer: string
  explanation: string
  leak_categories: string[]
}

interface Feedback {
  score: number
  isCorrect: boolean
  feedback: string
  keyConceptMissed: string
  studyRecommendation: string
}

type Phase = 'loading' | 'ready' | 'answering' | 'submitting' | 'result' | 'error'

// ─── Constants ─────────────────────────────────────────────────────────────────

const TIME_LIMIT_SECONDS = 180

const SUIT_COLORS: Record<string, string> = {
  h: '#e05252',  // hearts
  d: '#e05252',  // diamonds
  s: '#c8d0e0',  // spades
  c: '#52c2a0',  // clubs
}
const SUIT_SYMBOLS: Record<string, string> = {
  h: '♥', d: '♦', s: '♠', c: '♣',
}
const DIFFICULTY_CONFIG = {
  beginner: { label: 'Beginner', color: '#52c2a0' },
  intermediate: { label: 'Intermediate', color: '#e0a852' },
  advanced: { label: 'Advanced', color: '#e05252' },
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function PlayingCard({ card }: { card: string }) {
  if (!card || card.length < 2) return null
  const rank = card.slice(0, -1).toUpperCase()
  const suit = card.slice(-1).toLowerCase()
  const color = SUIT_COLORS[suit] ?? '#c8d0e0'
  const symbol = SUIT_SYMBOLS[suit] ?? suit

  return (
    <span style={{
      display: 'inline-flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      width: 44, height: 60,
      background: '#1a1f2e', border: '1px solid #2a3045',
      borderRadius: 6, fontFamily: 'Georgia, serif',
      margin: '0 3px', flexShrink: 0,
    }}>
      <span style={{ color, fontSize: 16, fontWeight: 700, lineHeight: 1 }}>{rank}</span>
      <span style={{ color, fontSize: 18, lineHeight: 1 }}>{symbol}</span>
    </span>
  )
}

function BoardCards({ boardStr }: { boardStr: string }) {
  const cards = boardStr.trim().split(/\s+/).filter(Boolean)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      {cards.map((c, i) => <PlayingCard key={i} card={c} />)}
    </div>
  )
}

function CircleTimer({ secondsLeft, total }: { secondsLeft: number; total: number }) {
  const pct = secondsLeft / total
  const r = 24
  const circ = 2 * Math.PI * r
  const dash = circ * pct
  const color = pct > 0.5 ? '#52c2a0' : pct > 0.2 ? '#e0a852' : '#e05252'
  const mins = Math.floor(secondsLeft / 60)
  const secs = secondsLeft % 60

  return (
    <div style={{ position: 'relative', width: 60, height: 60 }}>
      <svg width={60} height={60} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={30} cy={30} r={r} fill="none" stroke="#2a3045" strokeWidth={4} />
        <circle
          cx={30} cy={30} r={r}
          fill="none" stroke={color} strokeWidth={4}
          strokeDasharray={`${dash} ${circ}`}
          style={{ transition: 'stroke-dasharray 0.5s linear, stroke 0.5s' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Georgia, monospace',
        fontSize: 13, fontWeight: 700, color,
      }}>
        {mins}:{String(secs).padStart(2, '0')}
      </div>
    </div>
  )
}

function ScoreRing({ score }: { score: number }) {
  const r = 40
  const circ = 2 * Math.PI * r
  const dash = circ * (score / 100)
  const color = score >= 80 ? '#52c2a0' : score >= 50 ? '#e0a852' : '#e05252'

  return (
    <div style={{ position: 'relative', width: 96, height: 96 }}>
      <svg width={96} height={96} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={48} cy={48} r={r} fill="none" stroke="#2a3045" strokeWidth={6} />
        <circle
          cx={48} cy={48} r={r}
          fill="none" stroke={color} strokeWidth={6}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s ease' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ fontSize: 24, fontWeight: 800, color, fontFamily: 'Georgia, serif' }}>{score}</span>
        <span style={{ fontSize: 10, color: '#7a8aaa', letterSpacing: 1 }}>SCORE</span>
      </div>
    </div>
  )
}

// ─── Reusable: ExerciseRecap ──────────────────────────────────────────────────
// Bloque que muestra TODA la info del ejercicio: scenario + action sequence +
// hole cards + question. Lo usamos en la fase 'ready' (siempre visible) y
// en la fase 'result' (toggleable). Extraído como sub-componente para
// asegurar consistencia visual entre las dos.

function ExerciseRecap({ exercise }: { exercise: QuizExercise }) {
  return (
    <>
      {exercise.scenario && (
        <p style={{ color: '#7a8aaa', marginBottom: 16, fontSize: 14, lineHeight: 1.5 }}>
          {exercise.scenario}
        </p>
      )}

      {/* Action sequence */}
      <div style={styles.streetList}>
        {exercise.action_sequence.map((step, i) => (
          <div key={i} style={styles.streetRow}>
            <span style={styles.streetLabel}>{step.street}</span>
            {step.board && (
              <div style={{ marginBottom: 6 }}>
                <BoardCards boardStr={step.board} />
              </div>
            )}
            <span style={{ color: '#b0bad0', fontSize: 14 }}>{step.action}</span>
          </div>
        ))}
      </div>

      {/* Hole cards */}
      {exercise.show_hole_cards && exercise.hole_cards && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: '#7a8aaa', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
            Your Hand
          </p>
          <div style={{ display: 'flex' }}>
            {exercise.hole_cards.trim().split(/\s+/).map((c, i) => (
              <PlayingCard key={i} card={c} />
            ))}
          </div>
        </div>
      )}

      {/* Question */}
      <div style={styles.questionBox}>
        <p style={{ color: '#7a8aaa', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
          Question
        </p>
        <p style={{ color: '#e8edf5', fontSize: 15, fontWeight: 600, lineHeight: 1.5 }}>
          {exercise.question}
        </p>
      </div>
    </>
  )
}

// ─── Main Component ────────────────────────────────────────────────────────────

export function DailyQuiz() {
  const [phase, setPhase] = useState<Phase>('loading')
  const [exercise, setExercise] = useState<QuizExercise | null>(null)
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [openAnswer, setOpenAnswer] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(TIME_LIMIT_SECONDS)
  const [timeSpent, setTimeSpent] = useState(0)
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  const [errorMsg, setErrorMsg] = useState('')
  // Toggle to show/hide the question recap on the result screen.
  // Default: hidden (avoid saturating the result view), but one click reveals
  // all the original context so the user can re-read it while comparing
  // their answer against the GTO explanation.
  const [showQuestionRecap, setShowQuestionRecap] = useState(false)

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startTimeRef = useRef<number>(Date.now())

  useEffect(() => {
    fetchExercise()
  }, [])

  async function fetchExercise() {
    setPhase('loading')
    // Reset transient state when fetching a new exercise.
    setSelectedOption(null)
    setOpenAnswer('')
    setFeedback(null)
    setShowQuestionRecap(false)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) { setErrorMsg('No session'); setPhase('error'); return }
      const res = await fetch('/api/quiz-daily', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) {
        if (res.status === 429) {
          const data = await res.json()
          setErrorMsg(data.isPro
            ? 'Alcanzaste el límite de 10 desafíos por hoy. Volvé mañana.'
            : 'Usaste tus 3 desafíos gratuitos de hoy. Actualizá a Pro para más.')
          setPhase('error')
          return
        }
        throw new Error('No exercises available')
      }

      const data = await res.json()
      setExercise(data.exercise)
      setPhase('ready')
    } catch (e) {
      setErrorMsg((e as Error).message)
      setPhase('error')
    }
  }

  // ── Start quiz ──────────────────────────────────────────────────────────────
  function startQuiz() {
    setPhase('answering')
    setSecondsLeft(TIME_LIMIT_SECONDS)
    startTimeRef.current = Date.now()

    timerRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current!)
          handleSubmit(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  // ── Submit answer ───────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async (timedOut = false) => {
    if (!exercise) return
    clearInterval(timerRef.current!)
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) { setErrorMsg('No session'); setPhase('error'); return }
    const answer = exercise.options
      ? (selectedOption ?? '')
      : (timedOut ? '(No answer — time expired)' : openAnswer.trim())

    if (!answer && !timedOut) return

    setTimeSpent(Math.round((Date.now() - startTimeRef.current) / 1000))
    setPhase('submitting')

    try {
      const res = await fetch('/api/quiz-analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          exerciseId: exercise.id,
          userAnswer: answer || '(No answer)',
          timeSpentSeconds: Math.round((Date.now() - startTimeRef.current) / 1000),
        }),
      })

      if (!res.ok) throw new Error('Analysis failed')
      const data: Feedback = await res.json()
      setFeedback(data)
      setPhase('result')
    } catch (e) {
      setErrorMsg((e as Error).message)
      setPhase('error')
    }
  }, [exercise, selectedOption, openAnswer])

  // ── Cleanup timer on unmount ────────────────────────────────────────────────
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current) }, [])

  // ─── Render helpers ─────────────────────────────────────────────────────────

  const diffCfg = exercise ? DIFFICULTY_CONFIG[exercise.difficulty] : null

  // ─── Loading ─────────────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div style={styles.centeredPage}>
        <div style={styles.loadingSpinner} />
        <p style={{ color: '#7a8aaa', marginTop: 16 }}>Preparing your challenge…</p>
      </div>
    )
  }

  // ─── Error ───────────────────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div style={styles.centeredPage}>
        <div style={{ fontSize: 40 }}>⚠️</div>
        <p style={{ color: '#e05252', margin: '12px 0 4px' }}>Something went wrong</p>
        <p style={{ color: '#7a8aaa', marginBottom: 20, fontSize: 14 }}>{errorMsg}</p>
        <button className="btn btn-primary" onClick={fetchExercise}>Try Again</button>
      </div>
    )
  }

  if (!exercise) return null

  // ─── Ready ──────────────────────────────────────────────────────────────────
  if (phase === 'ready') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          {/* Header */}
          <div style={styles.cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ ...styles.badge, background: diffCfg!.color + '22', color: diffCfg!.color }}>
                {diffCfg!.label}
              </span>
              <span style={{ ...styles.badge, background: '#2a3045', color: '#7a8aaa' }}>
                {exercise.spot_type}
              </span>
              <span style={{ ...styles.badge, background: '#2a3045', color: '#7a8aaa' }}>
                {exercise.stack_depth}bb
              </span>
            </div>
            <span style={{ color: '#7a8aaa', fontSize: 13 }}>
              {exercise.position_hero} vs {exercise.position_villain}
            </span>
          </div>

          <h2 style={styles.quizTitle}>🃏 Daily Challenge</h2>

          <ExerciseRecap exercise={exercise} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
            <button className="btn btn-primary" onClick={startQuiz} style={{ flex: 1 }}>
              Start — {Math.floor(TIME_LIMIT_SECONDS / 60)}:00 Timer
            </button>
            <span style={{ color: '#7a8aaa', fontSize: 13 }}>
              {exercise.options ? `${exercise.options.length} options` : 'Open answer'}
            </span>
          </div>
        </div>
      </div>
    )
  }

  // ─── Answering ──────────────────────────────────────────────────────────────
  if (phase === 'answering') {
    const canSubmit = exercise.options
      ? !!selectedOption
      : openAnswer.trim().length > 10

    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <span style={{ ...styles.badge, background: diffCfg!.color + '22', color: diffCfg!.color }}>
                {diffCfg!.label}
              </span>
              <span style={{ ...styles.badge, background: '#2a3045', color: '#7a8aaa' }}>
                {exercise.spot_type} · {exercise.position_hero}v{exercise.position_villain} · {exercise.stack_depth}bb
              </span>
            </div>
            <CircleTimer secondsLeft={secondsLeft} total={TIME_LIMIT_SECONDS} />
          </div>

          <div style={styles.streetList}>
            {exercise.action_sequence.map((step, i) => (
              <div key={i} style={styles.streetRow}>
                <span style={styles.streetLabel}>{step.street}</span>
                {step.board && <div style={{ marginBottom: 4 }}><BoardCards boardStr={step.board} /></div>}
                <span style={{ color: '#b0bad0', fontSize: 14 }}>{step.action}</span>
              </div>
            ))}
          </div>

          {exercise.show_hole_cards && exercise.hole_cards && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ color: '#7a8aaa', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                Your Hand
              </p>
              <div style={{ display: 'flex' }}>
                {exercise.hole_cards.trim().split(/\s+/).map((c, i) => (
                  <PlayingCard key={i} card={c} />
                ))}
              </div>
            </div>
          )}

          <div style={styles.questionBox}>
            <p style={{ color: '#e8edf5', fontSize: 16, fontWeight: 600 }}>{exercise.question}</p>
          </div>

          <div style={{ marginTop: 20 }}>
            {exercise.options ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {exercise.options.map((opt, i) => {
                  const letter = String.fromCharCode(65 + i)
                  const isSelected = selectedOption === opt
                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedOption(opt)}
                      style={{
                        ...styles.optionBtn,
                        background: isSelected ? '#1e4a7a' : '#151b2a',
                        borderColor: isSelected ? '#4a9ae0' : '#2a3045',
                        color: isSelected ? '#e8f0f8' : '#b0bad0',
                      }}
                    >
                      <span style={{
                        ...styles.optionLetter,
                        background: isSelected ? '#4a9ae0' : '#2a3045',
                        color: isSelected ? '#fff' : '#7a8aaa',
                      }}>
                        {letter}
                      </span>
                      <span style={{ textAlign: 'left', flex: 1 }}>{opt}</span>
                    </button>
                  )
                })}
              </div>
            ) : (
              <div>
                <p style={{ color: '#7a8aaa', fontSize: 12, marginBottom: 8 }}>
                  Write your answer — be specific about percentages, hands, or reasoning.
                </p>
                <textarea
                  value={openAnswer}
                  onChange={e => setOpenAnswer(e.target.value)}
                  placeholder="E.g. 'Approximately 35% of my range wants to call — specifically hands with pair + draw equity like 5d4d, and strong top pairs. Pure air folds here because pot odds require 31% equity...'"
                  style={styles.textarea}
                  rows={5}
                />
              </div>
            )}
          </div>

          {/* Submit */}
          <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
            <button
              className="btn btn-primary"
              onClick={() => handleSubmit(false)}
              disabled={!canSubmit}
              style={{ flex: 1, opacity: canSubmit ? 1 : 0.4 }}
            >
              Submit Answer
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ─── Submitting (analysis loading) ───────────────────────────────────────────
  if (phase === 'submitting') {
    return (
      <div style={styles.centeredPage}>
        <div style={styles.loadingSpinner} />
        <p style={{ color: '#7a8aaa', marginTop: 16 }}>Analyzing your answer…</p>
        <p style={{ color: '#4a5568', fontSize: 13, marginTop: 4 }}>Claude is reviewing your reasoning</p>
      </div>
    )
  }

  // ─── Result ─────────────────────────────────────────────────────────────────
  if (phase === 'result' && feedback) {
    const scoreColor = feedback.score >= 80 ? '#52c2a0' : feedback.score >= 50 ? '#e0a852' : '#e05252'

    return (
      <div style={styles.page}>
        <div style={styles.card}>
          {/* Score header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              <ScoreRing score={feedback.score} />
              <div>
                <p style={{ color: scoreColor, fontWeight: 700, fontSize: 18, marginBottom: 4 }}>
                  {feedback.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                </p>
                <p style={{ color: '#7a8aaa', fontSize: 13 }}>
                  Time: {Math.floor(timeSpent / 60)}:{String(timeSpent % 60).padStart(2, '0')}
                </p>
              </div>
            </div>

            {/* ─── Question recap toggle ─────────────────────────────────── */}
            {/* Single-click button to expand the original question + context.
                Inspired by Pairrd's pattern — the result view stays clean by
                default but the user can pull the full spot back into view
                while comparing their answer to the GTO explanation. */}
            <button
              onClick={() => setShowQuestionRecap(v => !v)}
              style={{
                ...styles.recapToggle,
                background: showQuestionRecap ? '#1e4a7a' : '#151b2a',
                borderColor: showQuestionRecap ? '#4a9ae0' : '#2a3045',
                color: showQuestionRecap ? '#e8f0f8' : '#b0bad0',
              }}
            >
              {showQuestionRecap ? '▲ Hide question' : '▼ Show question'}
            </button>
          </div>

          {/* Collapsible question recap — shows the exact same content as
              the 'ready' phase: scenario, action sequence (with all street
              boards/actions), hole cards, and the question text. */}
          {showQuestionRecap && (
            <div style={styles.recapBox}>
              <ExerciseRecap exercise={exercise} />
            </div>
          )}

          {/* Coach feedback */}
          <div style={{ ...styles.feedbackBox, borderColor: scoreColor + '44', background: scoreColor + '0a' }}>
            <p style={{ color: '#7a8aaa', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
              Coach Analysis
            </p>
            <p style={{ color: '#e8edf5', fontSize: 15, lineHeight: 1.6 }}>{feedback.feedback}</p>
          </div>

          {/* Key concept missed */}
          {feedback.keyConceptMissed && feedback.keyConceptMissed !== 'None' && (
            <div style={styles.conceptBox}>
              <span style={{ color: '#7a8aaa', fontSize: 12 }}>📌 Key concept: </span>
              <span style={{ color: '#e0a852', fontWeight: 600, fontSize: 14 }}>
                {feedback.keyConceptMissed}
              </span>
            </div>
          )}

          {/* Correct answer reveal */}
          <div style={{ marginTop: 16 }}>
            <p style={{ color: '#7a8aaa', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
              Correct Answer
            </p>
            <div style={{ ...styles.questionBox, borderColor: '#52c2a044', background: '#52c2a00a' }}>
              <p style={{ color: '#52c2a0', fontWeight: 600 }}>{exercise.correct_answer}</p>
            </div>
          </div>

          {/* GTO explanation */}
          <div style={{ marginTop: 16 }}>
            <p style={{ color: '#7a8aaa', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
              GTO Explanation
            </p>
            <p style={{ color: '#b0bad0', fontSize: 14, lineHeight: 1.7 }}>{exercise.explanation}</p>
          </div>

          {/* Study tip */}
          <div style={{ ...styles.conceptBox, marginTop: 16, borderColor: '#4a9ae044', background: '#4a9ae008' }}>
            <span style={{ color: '#7a8aaa', fontSize: 12 }}>📚 Study tip: </span>
            <span style={{ color: '#4a9ae0', fontSize: 14 }}>{feedback.studyRecommendation}</span>
          </div>

          {/* Leak tags */}
          {exercise.leak_categories.length > 0 && (
            <div style={{ marginTop: 16, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ color: '#7a8aaa', fontSize: 12, alignSelf: 'center' }}>Targets: </span>
              {exercise.leak_categories.map(lk => (
                <span key={lk} style={{ ...styles.badge, background: '#1e3a5a', color: '#7ab0d0', fontSize: 11 }}>
                  {lk.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            <button className="btn btn-primary" onClick={fetchExercise} style={{ flex: 1 }}>
              Next Challenge
            </button>
          </div>
        </div>
      </div>
    )
  }

  return null
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  page: {
    maxWidth: 700,
    margin: '0 auto',
    padding: '24px 16px',
  } as React.CSSProperties,

  centeredPage: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
    padding: 32,
  } as React.CSSProperties,

  card: {
    background: '#111520',
    border: '1px solid #1e2636',
    borderRadius: 12,
    padding: '28px 28px',
  } as React.CSSProperties,

  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    flexWrap: 'wrap' as const,
    gap: 8,
  } as React.CSSProperties,

  quizTitle: {
    color: '#e8edf5',
    fontSize: 22,
    fontWeight: 800,
    marginBottom: 10,
    fontFamily: 'Georgia, serif',
  } as React.CSSProperties,

  badge: {
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    display: 'inline-block',
    textTransform: 'capitalize' as const,
  } as React.CSSProperties,

  streetList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
    marginBottom: 20,
    borderLeft: '2px solid #2a3045',
    paddingLeft: 16,
  } as React.CSSProperties,

  streetRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
  } as React.CSSProperties,

  streetLabel: {
    color: '#4a9ae0',
    fontSize: 11,
    textTransform: 'uppercase' as const,
    letterSpacing: 1.5,
    fontWeight: 700,
    marginBottom: 2,
  } as React.CSSProperties,

  questionBox: {
    background: '#151b2a',
    border: '1px solid #2a3045',
    borderRadius: 8,
    padding: '14px 18px',
  } as React.CSSProperties,

  feedbackBox: {
    border: '1px solid',
    borderRadius: 8,
    padding: '14px 18px',
    marginBottom: 16,
  } as React.CSSProperties,

  conceptBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    background: '#1a2030',
    border: '1px solid #2a3045',
    borderRadius: 8,
    padding: '12px 16px',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,

  optionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '13px 16px',
    border: '1px solid',
    borderRadius: 8,
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontSize: 14,
    transition: 'all 0.15s ease',
    width: '100%',
  } as React.CSSProperties,

  optionLetter: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 13,
    flexShrink: 0,
    transition: 'all 0.15s ease',
  } as React.CSSProperties,

  textarea: {
    width: '100%',
    background: '#151b2a',
    border: '1px solid #2a3045',
    borderRadius: 8,
    padding: '12px 14px',
    color: '#e8edf5',
    fontSize: 14,
    lineHeight: 1.6,
    resize: 'vertical' as const,
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,

  loadingSpinner: {
    width: 36,
    height: 36,
    border: '3px solid #2a3045',
    borderTopColor: '#4a9ae0',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  } as React.CSSProperties,

  // ─── Question recap toggle (result phase) ─────────────────────────────────
  recapToggle: {
    padding: '8px 14px',
    border: '1px solid',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap' as const,
    fontFamily: 'inherit',
  } as React.CSSProperties,

  recapBox: {
    background: '#0e131e',
    border: '1px dashed #2a3045',
    borderRadius: 10,
    padding: '20px 20px 4px',
    marginBottom: 20,
  } as React.CSSProperties,
}
